import React, { useCallback, useRef, useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Heading, Spinner, Alert, AlertIcon, Button, HStack } from '@chakra-ui/react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useAuth } from '../../utils/AuthContext';
import { useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { subMonths } from 'date-fns';

const ExamResult = () => {
  const { auth } = useAuth();
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const toast = useToast();
  const navigate = useNavigate();
  const gridRef = useRef();

  const paginationNumberFormatter = useCallback((params) => {
    return "[" + params.value.toLocaleString() + "]";
  }, []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: "agTextColumnFilter",
    floatingFilter: true,
    cellClass: "no-border",
    resizable: true,
  }), []);

  const fetchExamResults = async (startDate, endDate) => {
    try {
      let url = `${process.env.REACT_APP_API_URL}/api/Results/marksheetrange`;
      if (startDate && endDate) {
        url += `?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });

      setRowData(response.data.$values || response.data);
    } catch (error) {
      setError('Error fetching exam results');
      console.error('Error fetching exam results:', error);
      toast({
        title: "Error fetching exam results",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExamResults();
  }, [auth.token, toast]);

  const handleViewMarksheet = (userId) => {
    navigate(`/admin/mark-sheet/${userId}`);
  };

  const handleViewExamResponse = (userId, paperSetId) => {
    // console.log("handleViewExamResponse",paperSetId);
    navigate(`/admin/exam-response/${userId}/${paperSetId}`);
  };

  const columnDefs = useMemo(() => [
    { headerName: 'Ref ID', field: 'user.refid' },
    { headerName: 'Name', field: 'user.name' },
    { headerName: 'Emp Code', field: 'user.empcode' },
    { headerName: 'DOJ', field: 'user.doj', valueFormatter: ({ value }) => new Date(value).toLocaleDateString() },
    { headerName: 'Designation', field: 'user.designation' },
    { headerName: 'Office', field: 'user.office' },
    { headerName: 'City', field: 'user.city' },
    { headerName: 'Email', field: 'user.email' },
    { headerName: 'Test Set', field: 'examAssignment.paperSetTitle' },
    { headerName: 'Exam Date', field: 'examAssignment.examDate', valueFormatter: ({ value }) => new Date(value).toLocaleDateString() },
    { headerName: 'Is Completed', field: 'examAssignment.isCompleted', valueFormatter: ({ value }) => (value ? 'Yes' : 'No') },
    { headerName: 'Submission Date', field: 'examAssignment.submissionDate', valueFormatter: ({ value }) => value ? new Date(value).toLocaleDateString() : 'N/A' },
    {
      headerName: 'Marksheet',
      field: 'marksheet',
      cellRenderer: (params) => (
        <Button size="sm" colorScheme="purple" onClick={() => handleViewMarksheet(params.data.user.userId)}>
          View Marksheet
        </Button>
      ),
    },
    {
      headerName: 'Exam Response',
      field: 'examresponse',
      cellRenderer: (params) => (
        <Button size="sm" colorScheme="green" onClick={() => handleViewExamResponse(params.data.user.userId, params.data.examAssignment.paperSetId)}>
          Exam Response
        </Button>
      ),
    },
  ], [handleViewMarksheet, handleViewExamResponse]);

  const filterResultsByDate = () => {
    const startDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
    const endDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0);
    fetchExamResults(startDate, endDate);
  };

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Heading mb={4}>Exam Results</Heading>
      <HStack spacing={4} mb={4}>
        <Box>
          <label>Select Month:</label>
          <DatePicker
            selected={selectedMonth}
            onChange={(date) => setSelectedMonth(date)}
            dateFormat="MMM yyyy"
            showMonthYearPicker
            className="mb-4 highlight-datepicker"
          />
        </Box>
        <Button colorScheme="blue" onClick={filterResultsByDate}>
          Result
        </Button>
      </HStack>
      <div className="ag-theme-alpine" style={{ height: 600, width: '100%' }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={10}
          paginationNumberFormatter={paginationNumberFormatter}
          animateRows={true}
        />
      </div>
    </Box>
  );
};

export default ExamResult;
